/**
 * EnhancedErrorBoundary - A comprehensive error monitoring component
 *
 * Captures three types of errors:
 * 1. Console Errors - Overrides console.error using Object.defineProperty
 * 2. Network Errors - Wraps window.fetch to monitor HTTP responses
 * 3. JavaScript Exceptions - Uses window error listeners and React ErrorBoundary
 *
 * All captured errors are displayed in a debug panel overlay
 */

import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  ComponentType,
  ReactElement,
  FunctionComponent,
  Component,
  ErrorInfo as ReactErrorInfo,
  createElement,
} from 'react';
// @ts-ignore - react-error-boundary module resolution issue
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

interface ErrorInfo {
  timestamp: Date;
  type: 'exception' | 'console' | 'network';
  message: string;
  stack?: string;
  url?: string;
  method?: string;
  status?: number;
  componentStack?: string;
}

// Define FallbackProps interface locally to avoid module resolution issues
interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

type ErrorBoundaryFallback =
  | ComponentType<FallbackProps>
  | ReactElement<unknown, string | FunctionComponent | typeof Component>
  | null
  | undefined;

interface EnhancedErrorBoundaryProps {
  children: ReactNode;
  resetKeys?: Array<unknown>;
  maxErrors?: number;
  captureConsoleErrors?: boolean;
  captureNetworkErrors?: boolean; // Default: true - captures failed network requests (4xx, 5xx)
  fallback?: ErrorBoundaryFallback;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
  onReset?: (...args: any[]) => void;
}

// Error capture limits to prevent infinite loops:
// - Console errors: 20 (higher limit as they're often warnings/debug logs and less critical)
// - Network errors: 10 (moderate limit as failed requests can cascade but are important to track)
// - Exceptions: 10 (lower limit as JS exceptions can trigger rapid cascading failures)
// These limits prevent memory exhaustion and infinite error capture loops while still
// providing useful debugging information. Limits reset when "Try Again" is clicked.

// Circular buffer implementation for efficient error management
class CircularBuffer<T> {
  private buffer: (T | undefined)[];
  private head = 0;
  private tail = 0;
  private size = 0;
  private readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buffer = new Array(capacity);
  }

  push(item: T): void {
    this.buffer[this.tail] = item;
    this.tail = (this.tail + 1) % this.capacity;

    if (this.size < this.capacity) {
      this.size++;
    } else {
      this.head = (this.head + 1) % this.capacity;
    }
  }

  toArray(): T[] {
    const result: T[] = [];
    let current = this.head;

    for (let i = 0; i < this.size; i++) {
      const item = this.buffer[current];
      if (item !== undefined) {
        result.push(item);
      }
      current = (current + 1) % this.capacity;
    }

    return result;
  }

  clear(): void {
    this.buffer = new Array(this.capacity);
    this.head = 0;
    this.tail = 0;
    this.size = 0;
  }

  getSize(): number {
    return this.size;
  }
}

// Error deduplication helper
const getErrorKey = (error: ErrorInfo): string => {
  return `${error.type}-${error.message}-${error.url || ''}`;
};

// Format timestamp utility
const formatTimestamp = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
};

// Get error icon based on error type
const getErrorIcon = (type: ErrorInfo['type']): string => {
  switch (type) {
    case 'exception':
      return '⚠️';
    case 'console':
      return '🖥️';
    case 'network':
      return '🌐';
    default:
      return '❓';
  }
};

// ErrorItem component for displaying individual errors
interface ErrorItemProps {
  error: ErrorInfo;
  isCompact?: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const ErrorItem = ({ error, isCompact = false, isExpanded, onToggleExpanded }: ErrorItemProps) => {
  const textSize = isCompact ? 'text-xs' : 'text-sm';
  const iconSize = isCompact ? 'text-base' : 'text-lg';

  return (
    <div className="bg-white border border-red-200 rounded p-3 text-xs">
      <div className={isCompact ? 'flex items-start gap-2' : 'flex items-start gap-2 mb-2'}>
        <span className={iconSize}>{getErrorIcon(error.type)}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-red-600 font-medium">
            <span className={`uppercase ${textSize}`}>{error.type}</span>
            <span className={`text-gray-500 ${textSize}`}>{formatTimestamp(error.timestamp)}</span>
          </div>

          <div className="mt-1">
            {error.message.length > 150 && !isExpanded ? (
              <>
                <p className={`text-gray-800 break-words ${textSize}`}>
                  {error.message.substring(0, 150)}...
                </p>
                <button
                  onClick={onToggleExpanded}
                  className="text-red-600 hover:text-red-800 text-xs font-medium mt-1"
                >
                  Show more
                </button>
              </>
            ) : (
              <>
                <p className={`text-gray-800 break-words ${textSize}`}>{error.message}</p>
                {error.message.length > 150 && (
                  <button
                    onClick={onToggleExpanded}
                    className="text-red-600 hover:text-red-800 text-xs font-medium mt-1"
                  >
                    Show less
                  </button>
                )}
              </>
            )}
          </div>

          {error.url && (
            <div className={`mt-2 text-gray-600 ${textSize}`}>
              <span className="font-medium">URL:</span> {error.url}
              {error.method && <span className="ml-2">({error.method})</span>}
              {error.status && <span className="ml-2">Status: {error.status}</span>}
            </div>
          )}

          {error.stack && (
            <details className="mt-2">
              <summary className={`cursor-pointer text-gray-600 hover:text-gray-800 ${textSize}`}>
                Stack trace
              </summary>
              <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                {error.stack}
              </pre>
            </details>
          )}

          {error.componentStack && (
            <details className="mt-2">
              <summary className={`cursor-pointer text-gray-600 hover:text-gray-800 ${textSize}`}>
                Component stack
              </summary>
              <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                {error.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom hook for error tracking without global interception
const useErrorTracking = (maxErrors: number = 10) => {
  const errorBufferRef = useRef(new CircularBuffer<ErrorInfo>(maxErrors));
  const errorKeysRef = useRef(new Set<string>());
  const lastErrorTimeRef = useRef(0);
  const isUpdatingRef = useRef(false);
  const ERROR_THROTTLE_MS = 100;

  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  const addError = useCallback((error: ErrorInfo) => {
    // Prevent recursive updates
    if (isUpdatingRef.current) {
      return;
    }

    // Throttle rapid error additions
    const now = Date.now();
    const timeSinceLastError = now - lastErrorTimeRef.current;
    if (timeSinceLastError < ERROR_THROTTLE_MS) {
      return;
    }
    lastErrorTimeRef.current = now;

    // Deduplicate errors
    const errorKey = getErrorKey(error);
    if (errorKeysRef.current.has(errorKey)) {
      return;
    }

    errorKeysRef.current.add(errorKey);
    errorBufferRef.current.push(error);

    // Update state with new array from circular buffer
    isUpdatingRef.current = true;
    const newErrors = errorBufferRef.current.toArray();
    setErrors(newErrors);
    // Reset flag after a microtask
    // Use setTimeout instead of Promise to avoid ESLint warnings
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, []);

  const clearErrors = useCallback(() => {
    errorKeysRef.current.clear();
    errorBufferRef.current.clear();
    setErrors([]);
  }, []);

  return { errors, addError, clearErrors };
};

// Custom hook for console error monitoring using Proxy instead of global override
// How it works: Intercepts console.error by replacing it with a custom function that:
// 1. Captures the error details before logging
// 2. Calls the original console.error to maintain normal behavior
// 3. Uses Object.defineProperty to override console.error method
const useConsoleErrorMonitor = (
  enabled: boolean,
  addError: (error: ErrorInfo) => void,
  resetCounterRef?: React.MutableRefObject<(() => void) | null>,
) => {
  const consoleErrorCountRef = useRef(0);
  const MAX_CONSOLE_ERRORS = 20; // Allow more console errors as they're often less critical

  // Expose reset function
  useEffect(() => {
    if (resetCounterRef) {
      resetCounterRef.current = () => {
        consoleErrorCountRef.current = 0;
      };
    }
    return () => {
      if (resetCounterRef) {
        resetCounterRef.current = null;
      }
    };
  }, [resetCounterRef]);

  useEffect(() => {
    if (!enabled) return;

    // Reset count on mount
    consoleErrorCountRef.current = 0;

    // Create a proxy for console methods without modifying the global object
    // eslint-disable-next-line no-console
    const originalError = console.error.bind(console);
    const errorHandler = (...args: any[]) => {
      // Check if we've hit the limit
      if (consoleErrorCountRef.current >= MAX_CONSOLE_ERRORS) {
        // Still call the original method but don't capture
        originalError(...args);
        return;
      }

      const message = args
        .map(arg => {
          if (typeof arg === 'object') {
            try {
              // Handle circular references and special objects
              const seen = new WeakSet();
              return JSON.stringify(arg, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                  if (seen.has(value)) {
                    return '[Circular]';
                  }
                  seen.add(value);
                }
                return value;
              });
            } catch (e) {
              // Fallback for objects that can't be stringified
              return arg?.constructor?.name || '[Object]';
            }
          }
          return String(arg);
        })
        .join(' ');

      consoleErrorCountRef.current++;

      // Log when we hit the limit
      if (consoleErrorCountRef.current === MAX_CONSOLE_ERRORS) {
        // eslint-disable-next-line no-console
        console.warn('EnhancedErrorBoundary: Stopped capturing console errors after 20 errors');
      }

      const errorInfo = {
        timestamp: new Date(),
        type: 'console' as const,
        message,
        stack: new Error().stack,
      };

      addError(errorInfo);

      // Still call the original method
      originalError(...args);
    };

    // Override only within this component's scope
    const descriptor = Object.getOwnPropertyDescriptor(console, 'error');
    Object.defineProperty(console, 'error', {
      ...(descriptor || {}),
      value: errorHandler,
      configurable: true,
      writable: true,
    });

    return () => {
      // Restore original console.error
      Object.defineProperty(console, 'error', {
        ...(descriptor || {}),
        value: originalError,
        configurable: true,
        writable: true,
      });
    };
  }, [enabled, addError]);
};

// Custom hook for network monitoring using fetch wrapper instead of global override
// How it works: Overrides the global window.fetch method with a wrapper that:
// 1. Intercepts all fetch calls before they execute
// 2. Monitors response status codes and network errors
// 3. Captures failed requests (4xx, 5xx) and network failures
// 4. Restores original fetch on cleanup to prevent memory leaks
const useFetchMonitor = (
  enabled: boolean,
  addError: (error: ErrorInfo) => void,
  resetCounterRef?: React.MutableRefObject<(() => void) | null>,
) => {
  const originalFetchRef = useRef<typeof fetch | null>(null);
  const networkErrorCountRef = useRef(0);
  const lastNetworkErrorRef = useRef<{ url: string; time: number } | null>(null);

  // Expose reset function
  useEffect(() => {
    if (resetCounterRef) {
      resetCounterRef.current = () => {
        networkErrorCountRef.current = 0;
        lastNetworkErrorRef.current = null;
      };
    }
    return () => {
      if (resetCounterRef) {
        resetCounterRef.current = null;
      }
    };
  }, [resetCounterRef]);

  // Helper functions to reduce complexity
  const extractUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input instanceof Request) return input.url;
    return 'unknown';
  };

  const shouldSkipUrl = (url: string): boolean => {
    const skipPatterns = [
      '_next',
      'webpack',
      'hot-update',
      'sockjs',
      'datadoghq.com',
      'datadog',
      'dd-rum',
      'google-analytics',
      'analytics',
      'sentry.io',
    ];
    return skipPatterns.some(pattern => url.includes(pattern));
  };

  useEffect(() => {
    if (!enabled) return;

    const isRecentDuplicate = (url: string, now: number): boolean => {
      return !!(
        lastNetworkErrorRef.current &&
        lastNetworkErrorRef.current.url === url &&
        now - lastNetworkErrorRef.current.time < 1000
      );
    };

    const handleFailedResponse = (response: Response, url: string, init?: RequestInit): void => {
      const now = Date.now();

      if (isRecentDuplicate(url, now)) return;

      if (networkErrorCountRef.current >= 50) {
        // eslint-disable-next-line no-console
        console.warn('EnhancedErrorBoundary: Stopped capturing network errors after 50 errors');
        return;
      }

      networkErrorCountRef.current++;
      lastNetworkErrorRef.current = { url, time: now };

      addError({
        timestamp: new Date(),
        type: 'network',
        message: `Network request failed: ${response.status} ${response.statusText}`,
        url,
        method: init?.method || 'GET',
        status: response.status,
      });
    };

    // Store the original fetch with proper binding
    originalFetchRef.current = window.fetch.bind(window);

    // Create a wrapped fetch function that monitors errors
    const wrappedFetch = async (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const url = extractUrl(input);

      // Skip internal React/dev server requests and monitoring services
      if (shouldSkipUrl(url)) {
        return originalFetchRef.current!(input, init);
      }

      try {
        const response = await originalFetchRef.current!(input, init);

        // Only capture failed requests
        if (!response.ok) {
          handleFailedResponse(response, url, init);
        }

        return response;
      } catch (error) {
        // Skip if we've hit the error limit
        if (networkErrorCountRef.current >= 50) {
          throw error;
        }

        networkErrorCountRef.current++;

        addError({
          timestamp: new Date(),
          type: 'network',
          message: `Network request error: ${
            error instanceof Error ? error.message : String(error)
          }`,
          url,
          method: init?.method || 'GET',
          stack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    };

    // Override the global fetch
    window.fetch = wrappedFetch;

    return () => {
      // Restore original fetch only if it's still our wrapped version
      // This prevents conflicts when multiple EnhancedErrorBoundary instances exist
      if (window.fetch === wrappedFetch && originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
      originalFetchRef.current = null;
      networkErrorCountRef.current = 0;
      lastNetworkErrorRef.current = null;
    };
  }, [enabled, addError]);

  return window.fetch;
};

// Main component implementation
interface ErrorBoundaryContentProps {
  children: ReactNode;
  errors: ErrorInfo[];
  clearErrors: () => void;
  resetErrorBoundary: () => void;
  fallback?: ErrorBoundaryFallback;
}

const ErrorBoundaryContent = ({
  children,
  errors,
  clearErrors,
  resetErrorBoundary,
  fallback,
}: ErrorBoundaryContentProps) => {
  const [mainExpanded, setMainExpanded] = useState(false);
  const [overlayExpanded, setOverlayExpanded] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());

  const toggleMainExpanded = useCallback(() => {
    setMainExpanded((prev: boolean) => !prev);
  }, []);

  const toggleOverlayExpanded = useCallback(() => {
    setOverlayExpanded((prev: boolean) => !prev);
  }, []);

  const toggleMessageExpanded = useCallback((index: number) => {
    setExpandedMessages((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const hasReactException = useMemo(
    () => errors.some(e => e.type === 'exception' && e.componentStack),
    [errors],
  );

  // Memoize the last exception to prevent recalculation
  const lastException = useMemo(
    () => errors.filter((e: ErrorInfo) => e.type === 'exception' && e.componentStack).pop(),
    [errors],
  );

  // If there's a React exception and a fallback is provided, use it
  if (hasReactException && fallback) {
    // Check if fallback is a React element (already instantiated)
    if (typeof fallback !== 'function') {
      return fallback;
    }

    // For function/class components, use createElement
    return createElement(fallback, {
      error: new Error(lastException?.message || 'Unknown error'),
      resetErrorBoundary,
    });
  }

  return (
    <>
      {hasReactException ? (
        <div className="my-4 border border-red-300 rounded-lg bg-red-50 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-red-800 font-semibold text-lg">Error loading task</h3>
              <button
                onClick={toggleMainExpanded}
                className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded hover:bg-red-100 transition-colors"
              >
                {mainExpanded ? 'Hide Details' : 'Show Details'}
              </button>
            </div>
            <p className="text-red-700 text-sm mb-3">
              {(() => {
                // Check if any error contains the specific "reading 'value'" error
                const hasValueError = errors.some(e =>
                  e.message.includes("Cannot read properties of undefined (reading 'value')"),
                );

                if (hasValueError) {
                  return "This error may be resolved by switching the Task Params Version. If that doesn't help, please contact support.";
                }
                return 'Please contact support if the error persists.';
              })()}
            </p>

            {mainExpanded && (
              <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                {errors.map((error: ErrorInfo, index: number) => (
                  <ErrorItem
                    key={index}
                    error={error}
                    isExpanded={expandedMessages.has(index)}
                    onToggleExpanded={() => toggleMessageExpanded(index)}
                  />
                ))}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={resetErrorBoundary}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm font-medium"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      ) : (
        children
      )}

      {/* Error notification overlay for non-blocking errors */}
      {errors.length > 0 && !hasReactException && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md">
          <div className="border border-red-300 rounded-lg bg-red-50 shadow-lg overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-red-800 font-semibold text-sm flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                    {errors.length}
                  </span>
                  Error{errors.length !== 1 ? 's' : ''} Detected
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={toggleOverlayExpanded}
                    className="text-red-600 hover:text-red-800 text-xs font-medium px-2 py-1 rounded hover:bg-red-100 transition-colors"
                  >
                    {overlayExpanded ? 'Hide' : 'Show'} Details
                  </button>
                  <button
                    onClick={clearErrors}
                    className="text-gray-600 hover:text-gray-800 text-xs font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {overlayExpanded && (
                <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                  {errors.map((error: ErrorInfo, index: number) => (
                    <ErrorItem
                      key={index}
                      error={error}
                      isCompact={true}
                      isExpanded={expandedMessages.has(index)}
                      onToggleExpanded={() => toggleMessageExpanded(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const EnhancedErrorBoundary = ({
  children,
  resetKeys = [],
  maxErrors = 50,
  captureConsoleErrors = true,
  captureNetworkErrors = true, // Enabled by default with built-in safeguards
  fallback,
  onError,
  onReset,
}: EnhancedErrorBoundaryProps) => {
  const { errors, addError, clearErrors } = useErrorTracking(maxErrors);
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
  const [hasRenderError, setHasRenderError] = useState(false);
  const prevResetKeysRef = useRef<Array<unknown>>(resetKeys);
  const resetConsoleCounterRef = useRef<(() => void) | null>(null);
  const resetNetworkCounterRef = useRef<(() => void) | null>(null);

  // Check if we have a React exception that could cause render loops
  useEffect(() => {
    const hasReactException = errors.some(
      (e: ErrorInfo) => e.type === 'exception' && e.componentStack,
    );
    if (hasReactException && !hasRenderError) {
      setHasRenderError(true);
    }
  }, [errors, hasRenderError]);

  // Monitor console errors (isolated approach) - keep monitoring even during errors
  useConsoleErrorMonitor(captureConsoleErrors, addError, resetConsoleCounterRef);

  // Monitor network errors (isolated approach) - only disable for React exceptions
  // This hook overrides window.fetch globally to capture network errors
  useFetchMonitor(captureNetworkErrors, addError, resetNetworkCounterRef);

  // Proper resetKeys comparison that handles array length changes
  useEffect(() => {
    const prevResetKeys = prevResetKeysRef.current;

    // Check if arrays are different (including length)
    const keysChanged =
      resetKeys.length !== prevResetKeys.length ||
      resetKeys.some((key, index) => key !== prevResetKeys[index]);

    if (keysChanged) {
      clearErrors();
      setErrorBoundaryKey((prev: number) => prev + 1);
      prevResetKeysRef.current = resetKeys;
    }
  }, [resetKeys, clearErrors]);

  // Handle window errors with limit
  // How it works: Uses native browser event listeners to capture JavaScript exceptions:
  // 1. window.addEventListener('error') - catches unhandled JavaScript errors
  // 2. window.addEventListener('unhandledrejection') - catches unhandled Promise rejections
  // 3. React ErrorBoundary onError - catches React component errors (via handleError callback)
  const exceptionCountRef = useRef(0);
  const MAX_EXCEPTIONS = 10; // Limit exceptions as they can cascade quickly

  useEffect(() => {
    // Reset count on mount
    exceptionCountRef.current = 0;

    const handleWindowError = (event: ErrorEvent) => {
      // Skip if we're in a render error state
      if (hasRenderError) {
        return;
      }

      if (exceptionCountRef.current >= MAX_EXCEPTIONS) {
        return; // Stop capturing after limit
      }

      exceptionCountRef.current++;

      if (exceptionCountRef.current === MAX_EXCEPTIONS) {
        // eslint-disable-next-line no-console
        console.warn('EnhancedErrorBoundary: Stopped capturing exceptions after 10 errors');
      }

      addError({
        timestamp: new Date(),
        type: 'exception',
        message: event.message,
        stack: event.error?.stack,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Skip if we're in a render error state
      if (hasRenderError) {
        return;
      }

      if (exceptionCountRef.current >= MAX_EXCEPTIONS) {
        return; // Stop capturing after limit
      }

      const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
      const stack = event.reason instanceof Error ? event.reason.stack : undefined;

      exceptionCountRef.current++;

      if (exceptionCountRef.current === MAX_EXCEPTIONS) {
        // eslint-disable-next-line no-console
        console.warn('EnhancedErrorBoundary: Stopped capturing exceptions after 10 errors');
      }

      addError({
        timestamp: new Date(),
        type: 'exception',
        message: `Unhandled Promise Rejection: ${message}`,
        stack,
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addError, hasRenderError]);

  const handleError = useCallback(
    (error: Error, errorInfo: ReactErrorInfo) => {
      // Set flag to prevent further error capture during render
      setHasRenderError(true);

      // Use the same exception counter
      if (exceptionCountRef.current >= MAX_EXCEPTIONS) {
        return; // Stop capturing after limit
      }

      exceptionCountRef.current++;

      if (exceptionCountRef.current === MAX_EXCEPTIONS) {
        // eslint-disable-next-line no-console
        console.warn('EnhancedErrorBoundary: Stopped capturing exceptions after 10 errors');
      }

      addError({
        timestamp: new Date(),
        type: 'exception',
        message: error.message,
        stack: error.stack,
        // @ts-expect-error @types/react@18 $FIXME: null isn't accepted here
        componentStack: errorInfo.componentStack,
      });
    },
    [addError],
  );

  const resetErrorBoundary = useCallback(() => {
    clearErrors();
    setErrorBoundaryKey((prev: number) => prev + 1);
    setHasRenderError(false); // Re-enable error capture
    // Reset all error counters
    exceptionCountRef.current = 0;
    if (resetConsoleCounterRef.current) {
      resetConsoleCounterRef.current();
    }
    if (resetNetworkCounterRef.current) {
      resetNetworkCounterRef.current();
    }
  }, [clearErrors]);

  return (
    <ReactErrorBoundary
      key={errorBoundaryKey}
      // @ts-expect-error @types/react@18 $FIXME
      onError={onError || handleError}
      onReset={onReset}
      fallbackRender={({
        error: _error,
        resetErrorBoundary: reset,
      }: {
        error: Error;
        resetErrorBoundary: () => void;
      }) => {
        // Use our custom reset that properly cleans up state
        const handleReset = () => {
          resetErrorBoundary();
          reset();
        };
        return (
          <ErrorBoundaryContent
            errors={errors}
            clearErrors={clearErrors}
            resetErrorBoundary={handleReset}
            fallback={fallback}
          >
            {children}
          </ErrorBoundaryContent>
        );
      }}
    >
      <ErrorBoundaryContent
        errors={errors}
        clearErrors={clearErrors}
        resetErrorBoundary={resetErrorBoundary}
        fallback={fallback}
      >
        {children}
      </ErrorBoundaryContent>
    </ReactErrorBoundary>
  );
};
